import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useSWR from "swr";
import { API, fetcher } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useCart } from "../context/CartContext";
import { categoryName } from "../data/site";
import { COLOUR_HEX } from "../lib/colours";

const SITE = "https://sparkbright.org";

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const { data: p, isLoading, error } = useSWR(id ? `${API}/products/${id}` : null, fetcher);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState(null);
  const [colour, setColour] = useState(null);

  useEffect(() => {
    setActive(0);
    setSize(null);
    setColour(null);
  }, [id]);

  const gallery = p ? [p.image, ...(p.images || [])].filter((x, i, a) => x && a.indexOf(x) === i) : [];
  const chosenSize = size || p?.sizes?.[1] || p?.sizes?.[0] || "One Size";
  const chosenColour = colour || p?.colours?.[0] || null;
  const soldOut = p?.stock != null && p.stock <= 0;
  const lowStock = !soldOut && p?.stock != null && p.stock <= 3;
  const onSale = p?.compare_at != null && p.compare_at > p.price;
  const description =
    p?.tagline || `${p?.name} — part of Sparkwear's pastel-perfect kidswear range, sizes 0–10Y. Soft, comfy and made to shine.`;

  useSeo({
    title: p ? `${p.name} — ${categoryName(p.category_id)}` : undefined,
    description,
    image: p?.image,
    jsonLd: p
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.name,
          image: gallery,
          description,
          category: categoryName(p.category_id),
          brand: { "@type": "Brand", name: "Sparkwear" },
          offers: {
            "@type": "Offer",
            price: Number(p.price).toFixed(2),
            priceCurrency: "GBP",
            availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: `${SITE}/product/${p.id}`,
          },
        }
      : undefined,
  });

  const add = () => {
    if (!p || soldOut) return;
    addItem({
      productId: p.id,
      name: p.name,
      price: p.price,
      image: p.image,
      size: chosenSize,
      colour: chosenColour || "",
    });
    toast.success(`${p.name} added to your bag`, {
      description: `${chosenColour ? `${chosenColour} · ` : ""}Size ${chosenSize} · £${p.price.toFixed(2)}`,
    });
  };

  if (isLoading) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blush-600" />
      </main>
    );
  }

  if (error || !p) {
    return (
      <main className="mx-auto min-h-[60vh] w-full max-w-2xl px-5 py-20 text-center">
        <h1 className="font-display text-3xl font-semibold text-ink">Product not found</h1>
        <p className="mt-3 text-muted">This little piece may have flown off the shelves.</p>
        <Link
          to="/shop"
          data-testid="product-back-to-shop"
          className="mt-8 inline-flex rounded-full bg-ink px-8 py-3 font-display text-sm font-semibold text-white"
        >
          Back to shop
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-5 py-10 sm:px-8 sm:py-14">
      <Link
        to="/shop"
        data-testid="product-back-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted transition-colors duration-200 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to shop
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
        <section data-testid="product-gallery">
          <div className="overflow-hidden rounded-[32px] bg-blush-50">
            <img
              src={gallery[active]}
              alt={`${p.name} — view ${active + 1}`}
              className="aspect-[4/5] w-full object-cover"
            />
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
              {gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`product-thumb-${i}`}
                  aria-label={`Show photo ${i + 1}`}
                  className={`h-20 w-16 shrink-0 overflow-hidden rounded-2xl border-2 transition-colors duration-200 ${
                    active === i ? "border-blush-600" : "border-transparent"
                  }`}
                >
                  <img src={img} alt={`${p.name} thumbnail ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </section>

        <section data-testid="product-details" className="flex flex-col">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blush-700">
            {categoryName(p.category_id)}
          </p>
          <h1 className="mt-2 font-display text-3xl font-semibold leading-tight text-ink sm:text-4xl">
            {p.name}
          </h1>

          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-3xl font-semibold text-ink">£{p.price.toFixed(2)}</span>
            {onSale && <span className="text-lg text-muted line-through">£{p.compare_at.toFixed(2)}</span>}
            {onSale && (
              <span className="rounded-full bg-blush-600 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                Sale
              </span>
            )}
          </div>

          <p className="mt-5 leading-relaxed text-ink/75">{description}</p>

          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Size</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {(p.sizes || ["One Size"]).map((s) => (
                <button
                  key={s}
                  data-testid={`product-size-${s}`}
                  onClick={() => setSize(s)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    chosenSize === s ? "bg-ink text-white" : "bg-lav-50 text-muted hover:bg-lav-100"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {p.colours && p.colours.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Colour</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="product-colour-picker">
                {p.colours.map((c) => {
                  const chosen = chosenColour === c;
                  return (
                    <button
                      key={c}
                      data-testid={`product-colour-${c.toLowerCase()}`}
                      onClick={() => setColour(c)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                        chosen ? "bg-ink text-white" : "bg-lav-50 text-muted hover:bg-lav-100"
                      }`}
                    >
                      <span
                        className="h-3.5 w-3.5 rounded-full border border-ink/15"
                        style={{ backgroundColor: COLOUR_HEX[c.toLowerCase()] || "#E9E4F0" }}
                      />
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {soldOut ? (
            <p className="mt-6 rounded-2xl bg-blush-50 px-5 py-4 text-sm font-semibold text-blush-700" data-testid="product-sold-out">
              Out of stock — check back soon!
            </p>
          ) : (
            <button
              onClick={add}
              data-testid="product-add-to-bag"
              className="mt-6 w-full rounded-full bg-ink py-4 font-display text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:scale-[1.02]"
            >
              Add to Bag · £{p.price.toFixed(2)}
            </button>
          )}
          <p className="mt-3 text-sm font-semibold text-mint-600" data-testid="product-stock-note">
            {soldOut ? "" : lowStock ? `Only ${p.stock} left in stock! ⚡` : "In stock — 30-day happy returns"}
          </p>
          <p className="mt-2 text-xs text-muted" data-testid="product-delivery-note">
            Delivers worldwide — UK £4.99 (free over £50) · Europe £6.99 · rest of the world £8.99 (free over £100)
          </p>
        </section>
      </div>
    </main>
  );
}
